// frontend/src/pages/RevenueAnalysisPage.js
// Componente para el Análisis Avanzado de Ingresos.
// VERSIÓN FINAL: Rediseñado para ser dinámico y coherente con la nueva arquitectura.

import React, { useEffect, useState, useMemo } from 'react';
import { useDashboardStore } from '../store/dashboardStore';
import { useTemplateStore } from '../store/templateStore'; // Para obtener los tipos de iniciativa
import LoadingSpinner from '../components/LoadingSpinner';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { CalendarDaysIcon, PresentationChartLineIcon, ArrowLeftIcon } from '@heroicons/react/24/solid';

function RevenueAnalysisPage({ onBack }) {
  const { monthlyRevenueData, isLoadingMonthlyRevenue, fetchMonthlyRevenueData } = useDashboardStore();
  const { initiativeTypes, fetchInitiativeTypes } = useTemplateStore();

  const [filters, setFilters] = useState({
      period: '12m',
      initiativeTypeId: '',
  });

  useEffect(() => {
    fetchInitiativeTypes();
  }, [fetchInitiativeTypes]);

  useEffect(() => {
    fetchMonthlyRevenueData(filters);
  }, [fetchMonthlyRevenueData, filters]);
  
  const handleFilterChange = (name, value) => {
      setFilters(prev => ({ ...prev, [name]: value }));
  };

  const safeCurrencyFormatter = (value) => {
    if (typeof value !== 'number') return '$0.00';
    return `$${value.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const selectedInitiativeName = useMemo(() => {
    if (!filters.initiativeTypeId) return 'Todas las Iniciativas';
    return initiativeTypes.find(t => t.id === filters.initiativeTypeId)?.name || 'Iniciativas';
  }, [filters.initiativeTypeId, initiativeTypes]);

  return (
    <div className="p-6 bg-white rounded-lg shadow-xl w-full max-w-6xl mx-auto my-8">
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <h2 className="text-3xl font-extrabold text-gray-900">Análisis Detallado de Ingresos</h2>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center space-x-2 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>Volver al Dashboard</span>
        </button>
      </div>

      {/* Panel de Filtros */}
      <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
        <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Filtrar por Tipo de Iniciativa</h3>
            <div className="flex items-center space-x-2">
                <button onClick={() => handleFilterChange('initiativeTypeId', '')} className={`px-4 py-2 text-sm font-medium rounded-md ${filters.initiativeTypeId === '' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>Todos</button>
                {initiativeTypes.map(type => (
                    <button key={type.id} onClick={() => handleFilterChange('initiativeTypeId', type.id)} className={`px-4 py-2 text-sm font-medium rounded-md ${filters.initiativeTypeId === type.id ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>{type.name}</button>
                ))}
            </div>
        </div>
        <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center"><CalendarDaysIcon className="h-5 w-5 mr-2"/>Seleccionar Período</h3>
            <select
              value={filters.period}
              onChange={(e) => handleFilterChange('period', e.target.value)}
              className="mt-1 block w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="1m">Último Mes</option>
              <option value="3m">Últimos 3 Meses</option>
              <option value="6m">Últimos 6 Meses</option>
              <option value="12m">Últimos 12 Meses</option>
            </select>
        </div>
      </div>

      {isLoadingMonthlyRevenue ? (
        <div className="flex justify-center items-center h-96">
          <LoadingSpinner size="lg" />
          <p className="ml-4 text-gray-700">Cargando datos de ingresos...</p>
        </div>
      ) : monthlyRevenueData.length === 0 ? (
        <p className="text-center text-gray-600 py-10">No hay datos de ingresos para el período y filtro seleccionados.</p>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <PresentationChartLineIcon className="h-6 w-6 text-gray-600" />
            <span>Evolución Mensual de Ingresos ({selectedInitiativeName})</span>
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={monthlyRevenueData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <defs>
                <linearGradient id="colorExpected" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorRealized" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={safeCurrencyFormatter} />
              <Tooltip formatter={safeCurrencyFormatter} />
              <Legend />
              <Area type="monotone" dataKey="Total_Expected" stroke="#3b82f6" fillOpacity={1} fill="url(#colorExpected)" name="Total Esperado" />
              <Area type="monotone" dataKey="Total_Realized" stroke="#22c55e" fillOpacity={1} fill="url(#colorRealized)" name="Total Realizado" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default RevenueAnalysisPage;
